/**
 * Les conversions d'espace colorimétrique — **déplacer** une couleur, jamais
 * décider d'un contraste.
 *
 * La frontière avec `contrasteMarque.ts` n'est pas arbitraire : ce module
 * manipule la TEINTE (TSL), l'autre mesure la LUMINANCE PERÇUE (sRGB
 * linéarisé). Confondre les deux est exactement le défaut que `contrasteMarque`
 * a corrigé — un seuil posé sur la luminosité TSL rendait du blanc sur du jaune
 * à 1,43:1. Les séparer rend la confusion plus difficile à refaire.
 *
 * ⛔ **Aucune fonction d'ici ne doit servir à choisir une encre.**
 *
 * ⚠️ **L'échelle est 0-255**, celle de `canauxSRGB`, seule porte d'entrée du
 * dépôt. Un premier jet supposait `[0,1]` : il re-multipliait par 255 des
 * valeurs déjà en octets et toute couleur saturait — le teal `#006A63`
 * ressortait en cyan pur. Deux échelles pour une même donnée est une erreur
 * qu'aucune signature ne signale ; ce sont les tests de `brandTheme` qui l'ont
 * attrapée.
 */

/** `[r, g, b]` dans `[0,255]` → `#rrggbb`. */
export function hexDepuisCanaux([r, g, b]: [number, number, number]): string {
    const octet = (v: number) =>
        Math.round(Math.min(Math.max(v, 0), 255))
            .toString(16)
            .padStart(2, '0')
    return `#${octet(r)}${octet(g)}${octet(b)}`
}

/** `[r,g,b]` en octets → `[teinte 0-1, luminosité 0-1, saturation 0-1]`. */
export function rvbVersTsl(canaux: [number, number, number]): [number, number, number] {
    const [r, g, b] = canaux.map((v) => v / 255) as [number, number, number]
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return [0, l, 0]
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    const h =
        max === r
            ? ((g - b) / d + (g < b ? 6 : 0)) / 6
            : max === g
              ? ((b - r) / d + 2) / 6
              : ((r - g) / d + 4) / 6
    return [h, l, s]
}

/** L'inverse de `rvbVersTsl`, rendu directement en hex. */
export function tslVersHex(h: number, l: number, s: number): string {
    const octets = (v: number) => v * 255
    if (s === 0) return hexDepuisCanaux([octets(l), octets(l), octets(l)])
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    const canal = (t: number) => {
        let x = t
        if (x < 0) x += 1
        if (x > 1) x -= 1
        if (x < 1 / 6) return p + (q - p) * 6 * x
        if (x < 1 / 2) return q
        if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
        return p
    }
    return hexDepuisCanaux([octets(canal(h + 1 / 3)), octets(canal(h)), octets(canal(h - 1 / 3))])
}
